/**
 * MOCK embed-config (no backend yet). Stands in for GET /public/embed-config?tenant=<id>.
 * Two identities prove the theming mechanism (theming.md §5):
 *
 *  - DEFAULT  → TownDesk brand green (#2F6B4F), contrast → warm white.
 *  - Linn Valley → lake-community blue (#1D4E6F), contrast → white (passes AA), per the P3 spec.
 *
 * The accent ramps here are hand-derived to mirror what the server would compute (darken ~8/16%
 * for hover/active; ~10% tint over paper for weak; contrast = the AA-passing of {white, ink}).
 * Swap this module for a real fetch when Inky's /public/embed-config lands.
 */
import type { EmbedConfig } from "./embedConfig.js";

export const DEFAULT_EMBED_CONFIG: EmbedConfig = {
  tenantId: "towndesk-demo",
  townName: "Your Town",
  privacyUrl: "#privacy",
  backgroundColor: "#FBFAF7",
  accent: {
    light: {
      base: "#2F6B4F",
      hover: "#265A42",
      active: "#1E4A36",
      weak: "#E7F0EA",
      contrast: "#FBFAF7",
    },
    dark: {
      base: "#5CB98C",
      hover: "#74C79E",
      active: "#8FD5B5",
      weak: "#16281F",
      contrast: "#0F1712",
    },
  },
};

/** The Linn Valley reskin — customer zero (CLAUDE.md), a lake-community blue. */
export const LINN_VALLEY_EMBED_CONFIG: EmbedConfig = {
  tenantId: "linnvalley",
  townName: "City of Linn Valley",
  privacyUrl: "#privacy",
  websiteUrl: "https://www.linnvalley.gov",
  backgroundColor: "#EEF4F8",
  accent: {
    light: {
      base: "#1D4E6F",
      hover: "#194461",
      active: "#153A53",
      weak: "#E3EDF3",
      contrast: "#FBFAF7", // white passes AA on this blue
    },
    dark: {
      base: "#63A9D6",
      hover: "#7CB8DE",
      active: "#96C7E6",
      weak: "#132430",
      contrast: "#0F1712",
    },
  },
};

const REGISTRY: Record<string, EmbedConfig> = {
  [DEFAULT_EMBED_CONFIG.tenantId]: DEFAULT_EMBED_CONFIG,
  [LINN_VALLEY_EMBED_CONFIG.tenantId]: LINN_VALLEY_EMBED_CONFIG,
  default: DEFAULT_EMBED_CONFIG,
  demo: DEFAULT_EMBED_CONFIG,
  lv: LINN_VALLEY_EMBED_CONFIG,
};

/**
 * Mocked GET /public/embed-config. Resolves the tenant from the `?tenant=` query param
 * (as the real launcher passes it) and falls back to the default brand.
 */
export async function fetchEmbedConfig(tenantId?: string): Promise<EmbedConfig> {
  const id = (tenantId ?? new URLSearchParams(window.location.search).get("tenant") ?? "default")
    .trim()
    .toLowerCase();
  // Simulate a fast, cached edge response.
  await new Promise((r) => setTimeout(r, 0));
  return REGISTRY[id] ?? DEFAULT_EMBED_CONFIG;
}
